// src/pages/Dashboard/Admin/DetailsAdherents/components/ReglementPanel.jsx

import React, { useMemo } from "react";
import DashboardDataTable from "./DashboardDataTable";

import {
  calculateReglementTotal,
  formatCurrency,
  getPaymentStatusClass,
} from "../utils/adherentDashboardUtils";

function StatusBadge({ status }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ring-1 ${getPaymentStatusClass(
        status
      )}`}
    >
      {status || "—"}
    </span>
  );
}

export default function ReglementPanel({ reglement, saison }) {
  const amounts = useMemo(() => {
    const lic = Number(reglement?.montant_lic_assurance || 0);
    const cot = Number(reglement?.montant_cotisation || 0);
    const reductionFoyer = Number(reglement?.reduction_foyer || 0);
    const passSport = Number(reglement?.pass_sport || 0);
    const reductionSaison = Number(reglement?.reduction_saison || 0);
    const total = calculateReglementTotal(reglement);

    return [
      { id: "lic", label: "Licence + assurance", value: lic },
      { id: "cot", label: "Cotisation", value: cot },
      { id: "foyer", label: "Réduction foyer", value: reductionFoyer },
      { id: "pass", label: "Pass’Sport", value: passSport },
      { id: "saison", label: "Réduction saison", value: reductionSaison },
      { id: "total", label: "Total", value: total, strong: true },
    ];
  }, [reglement]);

  if (!reglement) {
    return (
      <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-5 text-sm font-medium text-slate-600 dark:border-slate-800 dark:bg-slate-950/60 dark:text-slate-300">
        Aucun règlement trouvé pour la saison{" "}
        <span className="font-semibold">{saison || "—"}</span>.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid gap-3 md:grid-cols-4">
        <div className="rounded-2xl border border-indigo-200 bg-indigo-50 p-4 text-indigo-700 dark:border-indigo-500/20 dark:bg-indigo-500/10 dark:text-indigo-300">
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em]">
            Saison
          </p>
          <p className="mt-1 text-xl font-semibold text-slate-950 dark:text-white">
            {reglement?.saison || saison || "—"}
          </p>
        </div>

        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300">
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em]">
            Total
          </p>
          <p className="mt-1 text-xl font-semibold text-slate-950 dark:text-white">
            {formatCurrency(calculateReglementTotal(reglement))}
          </p>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950/60">
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
            Type
          </p>
          <p className="mt-1 text-xl font-semibold text-slate-950 dark:text-white">
            {reglement?.type_reglement || "—"}
          </p>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950/60">
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
            Statut
          </p>
          <div className="mt-2">
            <StatusBadge status={reglement?.statut_reglement} />
          </div>
        </div>
      </div>

      <DashboardDataTable
        minWidth="720px"
        columns={[
          {
            key: "label",
            label: "Ligne",
            render: (row) => (
              <span
                className={
                  row.strong
                    ? "font-semibold text-slate-900 dark:text-white"
                    : "font-semibold"
                }
              >
                {row.label}
              </span>
            ),
          },
          {
            key: "value",
            label: "Montant",
            render: (row) => (
              <span
                className={
                  row.strong
                    ? "font-semibold text-emerald-700 dark:text-emerald-300"
                    : ""
                }
              >
                {formatCurrency(row.value)}
              </span>
            ),
          },
        ]}
        rows={amounts}
      />

      <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950/60">
        <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
          Observations
        </p>

        <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-slate-700 dark:text-slate-300">
          {reglement?.observations || "—"}
        </p>
      </div>
    </div>
  );
}